import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { optionSchemaRequired } from '~/features/types';

const strictDateSchema = z.iso.date(ERROR_MESSAGES.INVALID_DATE);

const isValidDateString = (val: string | undefined): boolean => {
  if (!val) return true;
  return strictDateSchema.safeParse(val).success;
};

/**
 * _fields denotes data that should only be used for client facing logic.
 */

export const boardCertSchema = z
  .object({
    _tblBoardName: z.string().optional().nullable(),
    // These are form-only mirrors of nullable API strings. Hydration normally
    // converts null to '', but accepting null here keeps a stale/raw form state
    // in the friendly field-level validation path below instead of surfacing
    // Zod's "expected string, received null" implementation detail.
    _specialtyName: z.string().nullish(),
    _specialtyCertName: z.string().nullish(),
    _certName: z.string().nullish(),
    _subSpecialtyName: z.string().nullish(),
    _medicalBoardName: z.string().nullish(),
    _specialtyBoardName: z.string().nullish(),
    certName: z.string(),
    // [MCV-1186] AI-parsed records may serialize boolean-ish fields as null
    // (unknown-at-parse-time). Accept null at the schema so the edit form
    // validates instead of throwing "Invalid input: expected boolean,
    // received null" on save. The upstream transform normalizes known nulls
    // to defaults, but nullish() here makes the contract explicit for any
    // field we haven't mapped.
    validFlag: z.boolean().nullish(),
    warnings: z.record(z.string(), z.string()).nullish(),
    certNum: z
      .string()
      .max(64, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64) })
      .optional(),
    // [MCV-1145] `certType` and `status` were marked required in the UI but
    // accepted empty strings at the schema level. That let empty submissions
    // fall through to the superRefine below, which referenced a conditionally
    // rendered field (specialtyCertificate / subSpecialtyCertificate) that
    // the user couldn't even see yet. Making them truly required surfaces a
    // clear field-level error right on the visible inputs.
    certType: optionSchemaRequired,
    id: z.number().optional(),
    // [MCV-1387] react-select sets the field to `null` when the user clicks
    // the clear (X) button. `.optional()` accepts undefined but NOT null —
    // the top-level type check throws "Expected object, received null", which
    // short-circuits the schema and prevents the superRefine block below from
    // running. Result: clearing a required dropdown produced no error at any
    // path, so AsyncCreatableSelect had nothing to render and the user saw
    // no red text. `.nullish()` accepts both null and undefined so the
    // superRefine fires and surfaces the right field-level error.
    medicalBoard: z
      .object({
        label: z.string(),
        value: z.string(),
        id: z.string().optional(),
      })
      .nullish(),
    specialtyBoard: z
      .object({
        label: z.string(),
        value: z.string(),
        id: z.string().optional(),
      })
      .nullish(),
    specialty: z
      .object({
        label: z.string(),
        value: z.string(),
        id: z.string().optional(),
      })
      .nullish(),
    specialtyCertificate: z
      .object({
        label: z.string(),
        value: z.string(),
        id: z.string().optional(),
      })
      .nullish(),
    subSpecialtyCertificate: z
      .object({
        label: z.string(),
        value: z.string(),
        id: z.string().optional(),
      })
      .nullish(),
    status: optionSchemaRequired,
    notes: z.string().optional(),
    // [MCV-1186] AI-parsed records serialize unknown-at-parse-time booleans
    // as null. Accept null here; transformFormValuesToApi already coerces
    // to a concrete boolean before shipping to the server.
    mocReqFlag: z.boolean().nullable(),
    origCertDate: z
      .string({ error: ERROR_MESSAGES.REQUIRED })
      .min(1, { message: ERROR_MESSAGES.REQUIRED })
      .refine(isValidDateString, {
        message: ERROR_MESSAGES.INVALID_DATE,
      }),
    expiryDate: z
      .string()
      .optional()
      .refine(isValidDateString, { message: ERROR_MESSAGES.INVALID_DATE }),
    recertDate: z
      .string()
      .optional()
      .refine(isValidDateString, { message: ERROR_MESSAGES.INVALID_DATE }),
  })
  .superRefine((data, ctx) => {
    // medicalBoard, specialtyBoard, and (when SPECIALTY) specialty are marked
    // `required` in the form UI but were only `.optional()` in the schema, so
    // the red asterisk was cosmetic — submit still went through with an empty
    // hierarchy. Enforce the cascade here.
    if (!data.medicalBoard?.value) {
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.REQUIRED,
        path: ['medicalBoard', 'value'],
      });
    }

    // Specialty Board supports a free-text fallback (`_specialtyBoardName`)
    // for boards that aren't in the options list, so accept either.
    if (!data.specialtyBoard?.value && !data._specialtyBoardName) {
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.REQUIRED,
        path: ['specialtyBoard', 'value'],
      });
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.REQUIRED,
        path: ['_specialtyBoardName'],
      });
    }

    if (data.certType.value === 'SPECIALTY' && !data.specialty?.value && !data._specialtyName) {
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.REQUIRED,
        path: ['specialty', 'value'],
      });
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.REQUIRED,
        path: ['_specialtyName'],
      });
    }

    const isSub = data.certType.value === 'SUBSPECIALTY';
    // [MCV-1627] AsyncSelectOrInput renders a free-text input instead of a
    // select whenever the parent cascade has no id, and writes the typed value
    // to the `_`-prefixed field. The two checks above already accept that
    // fallback; this one did not, so a certificate that existed only as free
    // text (or arrived that way from an AI-parsed record) never satisfied the
    // check and the user could not leave step 1. Accept either shape.
    const hasCert = isSub
      ? !!data.subSpecialtyCertificate?.value || !!data._subSpecialtyName
      : !!data.specialtyCertificate?.value || !!data._specialtyCertName;
    if (!hasCert) {
      // [MCV-1186] QA called the generic "A certificate selection is required"
      // wording unclear — users confused it with the "Certificate Number"
      // TextInput. Name the specific dropdown so the user knows which field
      // to fill.
      // [MCV-1627] Both issues now carry the same plain sentence. The old
      // "... to save" variant was raised while the user was clicking Next,
      // not Save, which read as a vague failure unrelated to the button they
      // had just pressed.
      const message = isSub
        ? 'Subspecialty Certificate is required'
        : 'Specialty Certificate is required';
      ctx.addIssue({
        code: 'custom',
        message,
        path: [isSub ? 'subSpecialtyCertificate' : 'specialtyCertificate', 'value'],
      });
      ctx.addIssue({
        code: 'custom',
        message,
        path: [isSub ? '_subSpecialtyName' : '_specialtyCertName'],
      });
    }
  })
  .refine(
    (data) => {
      if (!data.expiryDate || !data.origCertDate) return true;
      if (!isValidDateString(data.origCertDate) || !isValidDateString(data.expiryDate)) return true;
      return data.origCertDate < data.expiryDate;
    },
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Original Certification Date', 'Expired Date'),
      path: ['origCertDate'],
    }
  )
  .refine(
    (data) => {
      if (!data.recertDate || !data.origCertDate) return true;
      if (!isValidDateString(data.origCertDate) || !isValidDateString(data.recertDate)) return true;
      return data.origCertDate < data.recertDate;
    },
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Original Certification Date', 'Recertified Date'),
      path: ['recertDate'],
    }
  )
  .refine(
    (data) => {
      if (!data.recertDate || !data.expiryDate) return true;
      if (!isValidDateString(data.recertDate) || !isValidDateString(data.expiryDate)) return true;
      return data.recertDate < data.expiryDate;
    },
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Recertification Date', 'Expired Date'),
      path: ['recertDate'],
    }
  )
  .refine((data) => {
    // [MCV-1627] Include the AsyncSelectOrInput free-text fallbacks the
    // certificate check above now accepts, so a typed certificate name does not
    // leave `certName` -- the value the certifications grid renders -- empty.
    data.certName =
      data.subSpecialtyCertificate?.label ||
      data._subSpecialtyName ||
      data.specialtyCertificate?.label ||
      data._specialtyCertName ||
      '';
    return true;
  });
